import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, AlertCircle, AlertTriangle } from 'lucide-react';
import { createContactWithCheck, getOrganizations, createOrganizationWithCheck } from '../../../lib/supabase/contacts';
import { Link } from 'react-router-dom';
import { getOrganizationProfile } from '../../../lib/supabase/organization';
import type { DuplicateCheckResult } from '../../../types/contacts';
import { useAuthStore } from '../../../store/authStore';
import { useEntity } from '../../../hooks/useEntity';

interface ContactFormProps {
  onClose: () => void;
  onSuccess: () => void;
  defaultType?: 'customer' | 'team' | 'supplier' | 'partner';
}

export default function ContactForm({ onClose, onSuccess, defaultType = 'customer' }: ContactFormProps) {
  const { user } = useAuthStore();
  const { entity } = useEntity();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    organizationId: '',
    type: defaultType || 'customer',
    title: '',
    notes: '',
    source: 'gekko'
  });

  const [organizations, setOrganizations] = useState<Array<{ id: string, name: string }>>([]);
  const [newOrgName, setNewOrgName] = useState('');
  const [showNewOrgInput, setShowNewOrgInput] = useState(false);
  const [emails, setEmails] = useState<Array<{ email: string, type: string }>>([]);
  const [phones, setPhones] = useState<Array<{ phone: string, type: string }>>([]);
  const [duplicates, setDuplicates] = useState<DuplicateCheckResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [orgLoading, setOrgLoading] = useState<boolean>(true);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [profileExists, setProfileExists] = useState<boolean | null>(null);
  const [checkingProfile, setCheckingProfile] = useState(false);
  const [showProfileRequiredModal, setShowProfileRequiredModal] = useState(false);
  const [companyOrganization, setCompanyOrganization] = useState<{id: string, name: string} | null>(null);
  const [ignoreDuplicates, setIgnoreDuplicates] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    checkCompanyProfile();
    loadOrganizations();
  }, [entity?.id]);

  const checkCompanyProfile = async () => {
    if (!entity?.id) return;
    
    try { 
      console.log('ContactForm: Checking company profile');
      setCheckingProfile(true);
      const profile = await getOrganizationProfile();
      console.log('ContactForm: Profile check result:', !!profile, profile);
      setProfileExists(!!profile);
      
      if (profile) {
        if (profile.organization) {
          setCompanyOrganization({
            id: profile.organization.id,
            name: profile.organization.name
          });
        }

        // If this is a team contact, automatically set the organization ID
        if (formData.type === 'team') {
          setFormData(prev => ({ ...prev, organizationId: profile.organization?.id || '' }));
        }
      }
      
      if (!profile) {
        setError('You need to set up your company profile before adding contacts.');
      }
    } catch (error) {
      console.error('Failed to check company profile:', error);
      setProfileExists(false);
      setError('You need to set up your company profile before adding contacts.');
    } finally {
      setCheckingProfile(false);
    }
  };

  const loadOrganizations = async () => {
    if (!entity?.id) return;
    try {
      console.log('Loading organizations for entity:', entity.id);
      setOrgLoading(true);
      setOrgError(null);
      const orgs = await getOrganizations(entity.id);
      console.log('Loaded organizations:', orgs?.length || 0, orgs);
      setOrganizations(orgs || []);
      
      if (formData.organizationId && formData.organizationId !== '' && !validateOrganizationId(formData.organizationId)) {
        setFormData(prev => ({ ...prev, organizationId: '' }));
        setError('Previously selected organization is no longer available. Please select another organization.');
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
      setOrgError('Failed to load organizations. Please try again.');
    } finally {
      setOrgLoading(false);
    }
  };

  const validateOrganizationId = (orgId: string | null): boolean => {
    if (!orgId || orgId === '') return true;
    
    // For team members, only allow company organization
    if (formData.type === 'team') {
      return companyOrganization?.id === orgId;
    }
    
    // For other types, check against loaded organizations
    return organizations.some(org => org.id === orgId) || 
           (companyOrganization?.id === orgId);
  };

  const createNewOrganization = async (name: string, entityId: string): Promise<string | null> => {
    if (!name.trim()) {
      throw new Error('Organization name is required');
    }

    try {
      setCreatingOrg(true);
      const orgData = {
        name: name.trim(),
        entity_id: entityId,
        type: formData.type === 'customer' ? 'client' : 
              formData.type === 'supplier' ? 'supplier' : 
              formData.type === 'partner' ? 'partner' : 'client'
      } as const;
      
      console.log('Creating organization with data:', orgData);
      const result = await createOrganizationWithCheck(orgData);
      
      if (!result.data) {
        throw new Error(result.error || 'Failed to create organization');
      }
      
      // Immediately reload organizations to ensure the new one is available
      await loadOrganizations();
      
      return result.data.id;
    } catch (error) {
      console.error('Failed to create organization:', error);
      throw new Error('Failed to create organization. Please try again.');
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('First name and last name are required');
      return;
    }
    
    setError(null);
    
    if (!user) {
      setError('You need to be signed in to create contacts.');
      return;
    }
    
    if (!entity?.id) {
      setError('No entity found. Please refresh and try again.');
      return;
    }
    
    if (!profileExists) {
      setError('You need to set up your company profile before adding contacts.');
      setShowProfileRequiredModal(true);
      return;
    }

    if (orgLoading) {
      setError('Please wait for organizations to load');
      return;
    }

    if (orgError) {
      setError('Please resolve organization loading errors before continuing');
      return;
    }

    if (!['customer', 'team', 'supplier', 'partner'].includes(formData.type)) {
      setError(`Invalid contact type: ${formData.type}. Please select a valid type.`);
      return;
    }

    // Validate organization ID based on contact type
    let finalOrgId: string | null = null;

    try {
      // For team contacts, company organization is required
      if (formData.type === 'team') {
        if (!companyOrganization?.id) {
          setError('Team contacts must be associated with your company profile. Please set up your company profile first.');
          setShowProfileRequiredModal(true);
          return;
        }
        finalOrgId = companyOrganization.id;
      } else {
        // For non-team contacts
        if (showNewOrgInput && newOrgName.trim()) {
          // Create new organization
          finalOrgId = await createNewOrganization(newOrgName, entity.id);
          if (!finalOrgId) {
            throw new Error('Failed to create organization');
          }
        } else if (formData.organizationId) {
          // Validate existing organization
          if (!validateOrganizationId(formData.organizationId)) {
            throw new Error('Selected organization is no longer available. Please select a different organization.');
          }
          finalOrgId = formData.organizationId;
        }
      }

      setLoading(true);

      const contactType = formData.type;
      const organizationRelationship = contactType === 'team' ? 'internal' : 'external';

      console.log('Creating contact with data:', {
        type: contactType,
        organization_relationship: organizationRelationship,
        organization_id: finalOrgId,
        entity_id: entity.id
      });

      const result = await createContactWithCheck(
        {
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          type: contactType,
          title: formData.title.trim(),
          organization_id: finalOrgId,
          notes: formData.notes.trim(),
          entity_id: entity.id,
          organization_relationship: organizationRelationship,
          source: formData.source
        },
        emails.filter(e => e.email.trim()).map(e => ({
          email: e.email.trim(),
          type: e.type
        })),
        phones.filter(p => p.phone.trim()).map(p => ({
          phone: p.phone.trim(),
          type: p.type
        }))
      );

      if (result.duplicates && !ignoreDuplicates && (
        result.duplicates.nameMatches.length > 0 || 
        result.duplicates.emailMatches.length > 0 || 
        result.duplicates.phoneMatches.length > 0
      )) {
        setDuplicates(result.duplicates);
        setLoading(false);
        return;
      }

      if (result.error) {
        throw new Error(result.error);
      }

      if (result.data) {
        setFormData({
          firstName: '',
          lastName: '',
          organizationId: '',
          type: 'customer',
          title: '',
          notes: ''
        });
        setEmails([]);
        setPhones([]);
        setIgnoreDuplicates(false);
        onSuccess();
      }
    } catch (error: any) {
      console.error('Failed to create contact:', error);
      setError(error.message || 'Failed to create contact. Please try again.');
      
      if (error.message.includes('organization')) {
        await loadOrganizations();
      }
    } finally {
      setLoading(false);
      setIgnoreDuplicates(false);
    }
  };

  const handleContactTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as 'customer' | 'team' | 'supplier' | 'partner';
    
    if (!['customer', 'team', 'supplier', 'partner'].includes(newType)) {
      setError('Invalid contact type selected');
      return;
    }
    
    if (newType === 'team' && companyOrganization) {
      console.log('Setting team type, using company organization:', companyOrganization.id);
      setFormData(prev => ({
        ...prev,
        type: newType,
        organizationId: companyOrganization.id
      }));
      setShowNewOrgInput(false);
    } else if (newType === 'team' && !companyOrganization) {
      console.log('Setting team type without company organization');
      setFormData(prev => ({
        ...prev,
        type: newType,
        organizationId: ''
      }));
      setShowNewOrgInput(false);
      setError('Team contacts must be associated with your company profile. Please set up your company profile first.');
      setShowProfileRequiredModal(true);
    } else {
      setFormData(prev => ({
        ...prev,
        type: newType,
        organizationId: (prev.organizationId === companyOrganization?.id) ? '' : prev.organizationId
      }));
    }
  };

  const renderDuplicateWarning = () => {
    if (!duplicates) return null;

    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4" data-testid="duplicate-warning">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-800">Potential Duplicates Found</h4>
            <div className="mt-2 text-sm text-yellow-700 space-y-1">
              {duplicates.nameMatches.length > 0 && (
                <p>• Similar names found: {duplicates.nameMatches.map(m => `${m.first_name} ${m.last_name}`).join(', ')}</p>
              )}
              {duplicates.emailMatches.length > 0 && (
                <p>• Matching emails found</p>
              )}
              {duplicates.phoneMatches.length > 0 && (
                <p>• Matching phone numbers found</p>
              )}
            </div>
            <div className="mt-3 flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-gray-500 hover:text-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        {checkingProfile && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-xl">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        )}
        
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Add New Contact</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        {orgError && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm mb-4">
            {orgError}
          </div>
        )}

        {renderDuplicateWarning()}

        {!profileExists && !checkingProfile && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-sm mb-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Company Profile Required</p>
                <p className="mt-1">You need to set up your company profile before adding contacts.</p>
                <div className="mt-2">
                  <Link 
                    to="/dashboard" 
                    state={{ section: 'operations', area: 'company' }}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Set Up Company Profile
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Type</label>
            <select
              value={formData.type}
              onChange={handleContactTypeChange}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
            >
              <option value="customer">Customer</option>
              <option value="team">Team</option>
              <option value="supplier">Supplier</option>
              <option value="partner">Partner</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Organization</label>
            <div className="mt-1 space-y-2">
              {orgLoading ? (
                <div className="flex items-center space-x-2 text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                  <span>Loading organizations...</span>
                </div>
              ) : (
                !showNewOrgInput ? (
                  <>
                    <select
                      value={formData.organizationId || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, organizationId: e.target.value }))}
                      className={`block w-full rounded-lg border border-gray-300 px-3 py-2 ${
                        organizations.length === 0 && !companyOrganization ? 'bg-gray-100 text-gray-500' : ''
                      }`}
                      disabled={formData.type === 'team'}
                    >
                      <option value="">Select Organization</option>
                      {formData.type === 'team' && companyOrganization ? (
                        <option value={companyOrganization.id}>{companyOrganization.name} (Your Company)</option>
                      ) : (
                        <>
                          {organizations && organizations.length > 0 ? organizations.map(org => (
                            <option key={org.id} value={org.id}>{org.name}</option>
                          )) : (
                            <option value="" disabled>No organizations available</option>
                          )}
                        </>
                      )}
                    </select>
                    {formData.type !== 'team' && (
                      <button
                        type="button"
                        onClick={() => setShowNewOrgInput(true)}
                        className="text-sm text-blue-500 hover:text-blue-600"
                      >
                        + Add New Organization
                      </button>
                    )}
                  </>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={newOrgName}
                      onChange={(e) => setNewOrgName(e.target.value)}
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2"
                      placeholder="Enter organization name"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewOrgInput(false);
                        setNewOrgName('');
                      }}
                      className="text-sm text-gray-500 hover:text-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                )
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Email Addresses</label>
              <button
                type="button"
                onClick={() => setEmails(prev => [...prev, { email: '', type: 'work' }])}
                className="text-sm text-blue-500 hover:text-blue-600"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {emails.map((email, index) => (
              <div key={index} className="flex space-x-2 mb-2">
                <input
                  type="email"
                  value={email.email}
                  onChange={(e) => {
                    const newEmails = [...emails];
                    newEmails[index].email = e.target.value;
                    setEmails(newEmails);
                  }}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2"
                  placeholder="Email address"
                />
                <select
                  value={email.type}
                  onChange={(e) => {
                    const newEmails = [...emails];
                    newEmails[index].type = e.target.value;
                    setEmails(newEmails);
                  }}
                  className="w-32 rounded-lg border border-gray-300 px-3 py-2"
                >
                  <option value="work">Work</option>
                  <option value="personal">Personal</option>
                  <option value="other">Other</option>
                </select>
                <button
                  type="button"
                  onClick={() => {
                    const newEmails = [...emails];
                    newEmails.splice(index, 1);
                    setEmails(newEmails);
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
            {emails.length === 0 && (
              <button
                type="button"
                onClick={() => setEmails([{ email: '', type: 'work' }])}
                className="text-sm text-blue-500 hover:text-blue-600"
              >
                + Add Email
              </button>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Phone Numbers</label>
              <button
                type="button"
                onClick={() => setPhones(prev => [...prev, { phone: '', type: 'work' }])}
                className="text-sm text-blue-500 hover:text-blue-600"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {phones.map((phone, index) => (
              <div key={index} className="flex space-x-2 mb-2">
                <input
                  type="tel"
                  value={phone.phone}
                  onChange={(e) => {
                    const newPhones = [...phones];
                    newPhones[index].phone = e.target.value;
                    setPhones(newPhones);
                  }}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2"
                  placeholder="Phone number"
                />
                <select
                  value={phone.type}
                  onChange={(e) => {
                    const newPhones = [...phones];
                    newPhones[index].type = e.target.value;
                    setPhones(newPhones);
                  }}
                  className="w-32 rounded-lg border border-gray-300 px-3 py-2"
                >
                  <option value="work">Work</option>
                  <option value="mobile">Mobile</option>
                  <option value="home">Home</option>
                  <option value="other">Other</option>
                </select>
                <button
                  type="button"
                  onClick={() => {
                    const newPhones = [...phones];
                    newPhones.splice(index, 1);
                    setPhones(newPhones);
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
            {phones.length === 0 && (
              <button
                type="button"
                onClick={() => setPhones([{ phone: '', type: 'work' }])}
                className="text-sm text-blue-500 hover:text-blue-600"
              >
                + Add Phone
              </button>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Source</label>
            <select
              value={formData.source}
              onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value }))}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
            >
              <option value="gekko">Gekko (Manual Entry)</option>
              <option value="import">Import</option>
              <option value="csv">CSV Upload</option>
              <option value="google">Google Contacts</option>
              <option value="outlook">Outlook</option>
              <option value="salesforce">Salesforce</option>
              <option value="hubspot">HubSpot</option>
              <option value="api">API</option>
              <option value="manual">Manual Entry</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">Track where this contact originated from</p>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || orgLoading || creatingOrg || !!orgError || profileExists === false}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2"
              onClick={() => duplicates && setIgnoreDuplicates(true)}
            >
              {loading || creatingOrg ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <span>{duplicates && (duplicates.nameMatches.length > 0 || duplicates.emailMatches.length > 0 || duplicates.phoneMatches.length > 0) ? 'Create Anyway' : 'Create Contact'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
      
      {showProfileRequiredModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Company Profile Required</h3>
              <p className="text-gray-600 mb-6">
                You need to set up your company profile before you can add contacts.
                Would you like to set up your company profile now?
              </p>
              <div className="flex justify-center space-x-4">
                <Link
                  to="/dashboard"
                  state={{ section: 'operations', area: 'company' }}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                >
                  Set Up Company Profile
                </Link>
                <button
                  onClick={() => setShowProfileRequiredModal(false)}
                  className="text-gray-600 hover:text-gray-800 px-4 py-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}